import { env } from '~/env'
import { getString } from '~/lib/storageUtils'
import {
  MbueRestAPI,
  type Submittal as SubmittalBase,
  type ProjectDocs,
  type CreateSubmittalParams,
  type ApiError,
  type WorkflowDefinition,
  type Workflow,
  type UpdateTaskCompletionStatusParams
} from 'mbue-rest-client'
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  type UseMutationOptions
} from '@tanstack/react-query'
import {
  createDocList,
  type DocListParams,
  updateWorkflowStepResult,
  type UpdateWorkflowStepResultParams
} from '~/lib/mbue-api/services/submittals'
import { type SubmittalDocList } from '~/lib/mbue-api/types'

export interface Submittal extends SubmittalBase {
  is_completed: boolean
}

const mbueApi = new MbueRestAPI({
  BASE: env.VITE_MBUE_API,
  TOKEN: getString('access_token')
})

export function useUpdateStepTaskResult(
  projectId: number,
  workflowInstanceId: string,
  stepIndex: number,
  taskIndex: number,
  options?: UseMutationOptions<Workflow, Error, UpdateTaskCompletionStatusParams>
) {
  return useMutation({
    mutationFn: (requestBody: UpdateTaskCompletionStatusParams) => {
      if (projectId === 0 || workflowInstanceId === '') {
        throw new Error('Project Id and Workflow Instance Id are required')
      }
      return mbueApi.submittal.updateTaskCompletionStatus(
        projectId,
        workflowInstanceId,
        stepIndex,
        taskIndex,
        requestBody
      )
    },
    ...options
  })
}

export function useFetchDocList(
  queryParams: { projectId: number },
  options?: Omit<UseQueryOptions<ProjectDocs, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['doc-list', queryParams.projectId],
    queryFn: () => mbueApi.submittal.getDocList(queryParams.projectId),
    ...options
  })
}

export function useCreateDocList(
  options?: UseMutationOptions<SubmittalDocList, Error, DocListParams>
) {
  return useMutation({
    mutationFn: (params: DocListParams) => createDocList(params),
    ...options
  })
}

export function useFetchWorkflowDefs(
  options?: Omit<UseQueryOptions<WorkflowDefinition[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['workflow-definitions'],
    queryFn: () => mbueApi.submittal.getWorkflowDefinitions(),
    ...options
  })
}

export function useFetchSubmittals(
  queryParams: { projectId: number },
  options?: Omit<UseQueryOptions<Submittal[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['submittals', queryParams.projectId],
    queryFn: async () => {
      const response = await mbueApi.submittal.getSubmittals(queryParams.projectId)
      const submittals = [...response] as unknown as Submittal[]
      return submittals
    },
    ...options
  })
}

export function useCreateSubmittal(
  options?: UseMutationOptions<
    Omit<Submittal, 'is_completed'>,
    ApiError,
    { project_id: number; requestBody: CreateSubmittalParams }
  >
) {
  return useMutation({
    mutationFn: (params: { project_id: number; requestBody: CreateSubmittalParams }) => {
      if (params.project_id === 0) {
        throw new Error('Project Id are required')
      }
      return mbueApi.submittal.createSubmittal(params.project_id, params.requestBody)
    },
    ...options
  })
}

const mapWorkflowDataToWorflowDef = (
  workflowData: Workflow,
  workflowDef: WorkflowDefinition
) => {
  const steps = workflowDef.steps
  const stepsWithData = steps.map((step, index) => {
    return {
      ...step,
      state: { ...workflowData.state[index] },
      data: { ...workflowData.data[index] }
    }
  })
  return {
    ...workflowDef,
    projectId: workflowData.project_id,
    steps: stepsWithData
  }
}

export type WorkflowDefWithData = ReturnType<typeof mapWorkflowDataToWorflowDef>

export function useFetchWorkflow(
  params: {
    projectId: number
    workflowInstanceId: string
  },
  options?: Omit<UseQueryOptions<WorkflowDefWithData, Error>, 'queryKey' | 'queryFn'> & {
    pollingInterval?: number | false
    refetchIntervalInBackground?: boolean
  }
) {
  const getWorkflow = async () => {
    const { projectId, workflowInstanceId } = params
    const workflow = await mbueApi.submittal.getWorkflow(projectId, workflowInstanceId)
    const workflowDefs = await mbueApi.submittal.getWorkflowDefinitions()

    const workflowDef = workflowDefs.find(
      (workflowItem) => workflowItem.definition_id === workflow.workflow_definition_id
    )

    if (!workflowDef) {
      throw new Error(
        `Workflow definition not found for workflow instance ${workflowInstanceId}`
      )
    }

    return mapWorkflowDataToWorflowDef(workflow, workflowDef)
  }

  const { pollingInterval, refetchIntervalInBackground, ...restOptions } = options ?? {}

  return useQuery({
    queryKey: ['workflow-instance', params.projectId, params.workflowInstanceId],
    queryFn: getWorkflow,
    refetchInterval: pollingInterval,
    refetchIntervalInBackground,
    ...restOptions
  })
}

export function useUpdateWorkflowStep(
  options?: UseMutationOptions<string, ApiError, UpdateWorkflowStepResultParams>
) {
  return useMutation({
    mutationFn: updateWorkflowStepResult,
    ...options
  })
}
